import { FALLBACK_DASH } from '../consts';

/**
 * Formats game scores with support for regular scores and penalty shootouts
 * Handles null values gracefully using fallback dash
 *
 * @param homeScore - Home team score (null if not available)
 * @param awayScore - Away team score (null if not available)
 * @param homePenalty - Home team penalty shootout score (optional)
 * @param awayPenalty - Away team penalty shootout score (optional)
 * @returns Formatted score string (e.g., "2 : 1", "2 : 2 (4 : 3)", "- : -")
 *
 * @example
 * formatScore(2, 1) // "2 : 1"
 * formatScore(2, 2, 4, 3) // "2 : 2 (4 : 3)"
 * formatScore(2, 2, 0, 0) // "2 : 2" (a 0 : 0 shootout score is not shown)
 * formatScore(null, null) // "- : -"
 * formatScore(2, null) // "2 : -"
 */
export const formatScore = (
  homeScore: number | null,
  awayScore: number | null,
  homePenalty?: number | null,
  awayPenalty?: number | null
): string => {
  // Format regular score with fallback for null values
  const homeDisplay = homeScore !== null ? String(homeScore) : FALLBACK_DASH;
  const awayDisplay = awayScore !== null ? String(awayScore) : FALLBACK_DASH;
  const regularScore = `${homeDisplay} : ${awayDisplay}`;

  // Add the penalty shootout only when both scores are present and are not a
  // 0 : 0 — a 0 : 0 shootout score means no shootout took place, so there is
  // nothing to show.
  const hasPenalties =
    homePenalty !== null &&
    homePenalty !== undefined &&
    awayPenalty !== null &&
    awayPenalty !== undefined &&
    !(homePenalty === 0 && awayPenalty === 0);

  if (hasPenalties) {
    const penaltyScore = `${homePenalty} : ${awayPenalty}`;
    return `${regularScore} (${penaltyScore})`;
  }

  return regularScore;
};
